import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker using static file in public folder
// This avoids CORS and MIME type issues by serving from same domain
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Constants
const DEFAULT_QUALITY = 0.9;
const DEFAULT_SCALE = 3.0;
const BASE64_SIZE_MULTIPLIER = 0.75;
const KB_CONVERSION = 1024;
const MAX_FILENAME_LENGTH = 100;

/**
 * Converts a PDF file to an array of image data URLs
 * @param {File} pdfFile - The PDF file to convert
 * @param {Object} options - Conversion options
 * @param {number} options.quality - Image quality (0-1, default: 0.9)
 * @param {number} options.scale - Rendering scale (default: 3.0)
 * @param {Function} options.onProgress - Progress callback (pageNumber, totalPages)
 * @returns {Promise<Array>} Array of converted page objects
 */
export const convertPdfToImages = async (pdfFile, options = {}) => {
    validatePdfFile(pdfFile);

    const { quality = DEFAULT_QUALITY, scale = DEFAULT_SCALE, onProgress = () => {} } = options;

    try {
        const pdf = await loadPdfDocument(pdfFile);
        const baseFileName = createBaseFileName(pdfFile.name);

        return await convertAllPages(pdf, baseFileName, { quality, scale, onProgress });
    } catch (error) {
        console.error('PDF conversion failed:', error);
        throw new Error(`Failed to convert PDF: ${error.message}`);
    }
};

/**
 * Loads PDF document from file
 * @param {File} pdfFile - PDF file to load
 * @returns {Promise<Object>} PDF document object
 */
const loadPdfDocument = async (pdfFile) => {
    const arrayBuffer = await pdfFile.arrayBuffer();
    return await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
};

/**
 * Converts all pages of a PDF document
 * @param {Object} pdf - PDF document
 * @param {string} baseFileName - Base name for generated files
 * @param {Object} options - Conversion options
 * @returns {Promise<Array>} Array of converted pages
 */
const convertAllPages = async (pdf, baseFileName, options) => {
    const { quality, scale, onProgress } = options;
    const totalPages = pdf.numPages;
    const images = [];

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const imageData = await convertPageToImage(page, { quality, scale });

        images.push({
            pageNumber,
            imageDataUrl: imageData.dataUrl,
            fileName: createPageFileName(baseFileName, pageNumber, totalPages),
            fileSize: imageData.size,
            originalFileName: pdf.filename || 'Unknown',
        });

        onProgress(pageNumber, totalPages);
    }

    return images;
};

/**
 * Creates base filename from PDF name
 * @param {string} pdfName - Original PDF filename
 * @returns {string} Sanitized base filename
 */
const createBaseFileName = (pdfName) => {
    return sanitizeFileName(pdfName.replace(/\.pdf$/i, ''));
};

/**
 * Creates filename for individual page
 * @param {string} baseFileName - Base filename
 * @param {number} pageNumber - Current page number
 * @param {number} totalPages - Total number of pages
 * @returns {string} Page filename
 */
const createPageFileName = (baseFileName, pageNumber, totalPages) => {
    const fileName = totalPages > 1 ? `${baseFileName} - Page ${pageNumber}` : baseFileName;
    return sanitizeFileName(fileName);
};

/**
 * Converts a single PDF page to an image data URL
 * @param {Object} page - PDF.js page object
 * @param {Object} options - Conversion options
 * @returns {Promise<Object>} Object with dataUrl and size
 */
export const convertPageToImage = async (page, options = {}) => {
    const { quality = DEFAULT_QUALITY, scale = DEFAULT_SCALE } = options;

    let canvas = null;

    try {
        canvas = createHighQualityCanvas(page, scale);
        await renderPageToCanvas(page, canvas, scale);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const size = calculateImageSize(dataUrl);

        return { dataUrl, size };
    } catch (error) {
        console.error('Page conversion failed:', error);
        throw new Error(`Failed to convert page: ${error.message}`);
    } finally {
        if (canvas) canvas.remove();
    }
};

/**
 * Creates a high-quality canvas for rendering
 * @param {Object} page - PDF page object
 * @param {number} scale - Rendering scale
 * @returns {HTMLCanvasElement} Configured canvas
 */
const createHighQualityCanvas = (page, scale) => {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Configure for high quality
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    return canvas;
};

/**
 * Renders PDF page to canvas
 * @param {Object} page - PDF page object
 * @param {HTMLCanvasElement} canvas - Canvas to render to
 * @param {number} scale - Rendering scale
 */
const renderPageToCanvas = async (page, canvas, scale) => {
    const viewport = page.getViewport({ scale });
    const renderContext = {
        canvasContext: canvas.getContext('2d'),
        viewport: viewport,
    };

    await page.render(renderContext).promise;
};

/**
 * Calculates image size from data URL
 * @param {string} dataUrl - Image data URL
 * @returns {number} Size in KB
 */
const calculateImageSize = (dataUrl) => {
    return Math.round((dataUrl.length * BASE64_SIZE_MULTIPLIER) / KB_CONVERSION);
};

/**
 * Validates PDF file input
 * @param {File} file - File to validate
 * @throws {Error} If file is invalid
 */
const validatePdfFile = (file) => {
    if (!file) {
        throw new Error('No file provided');
    }
    if (!isPdfFile(file)) {
        throw new Error('File must be a PDF');
    }
};

/**
 * Converts data URL to File object for upload
 * @param {string} dataUrl - Image data URL
 * @param {string} fileName - File name for the image
 * @returns {File} File object ready for upload
 */
export const dataUrlToFile = (dataUrl, fileName) => {
    try {
        const { data, mimeType } = parseDataUrl(dataUrl);
        const uint8Array = base64ToUint8Array(data);
        const finalFileName = ensureFileExtension(fileName, mimeType);

        return new File([uint8Array], finalFileName, { type: mimeType });
    } catch (error) {
        console.error('Data URL to file conversion failed:', error);
        throw new Error(`Failed to convert image data: ${error.message}`);
    }
};

/**
 * Parses data URL to extract mime type and data
 * @param {string} dataUrl - Data URL to parse
 * @returns {Object} Object with data and mimeType
 */
const parseDataUrl = (dataUrl) => {
    const [header, data] = dataUrl.split(',');
    const mimeType = header.match(/:(.*?);/)[1];
    return { data, mimeType };
};

/**
 * Converts base64 string to Uint8Array
 * @param {string} base64Data - Base64 encoded data
 * @returns {Uint8Array} Binary data array
 */
const base64ToUint8Array = (base64Data) => {
    const binaryString = atob(base64Data);
    const uint8Array = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i);
    }

    return uint8Array;
};

/**
 * Ensures filename has proper extension
 * @param {string} fileName - Original filename
 * @param {string} mimeType - File mime type
 * @returns {string} Filename with extension
 */
const ensureFileExtension = (fileName, mimeType) => {
    const sanitizedName = sanitizeFileName(fileName);
    const extension = mimeType === 'image/jpeg' ? '.jpg' : '.png';

    return sanitizedName.endsWith('.jpg') || sanitizedName.endsWith('.png')
        ? sanitizedName
        : `${sanitizedName}${extension}`;
};

/**
 * Validates if a file is a PDF
 * @param {File} file - File to validate
 * @returns {boolean} True if file is a PDF
 */
export const isPdfFile = (file) => {
    return file && file.type === 'application/pdf';
};

/**
 * Validates if a file is an image
 * @param {File} file - File to validate
 * @returns {boolean} True if file is an image
 */
export const isImageFile = (file) => {
    return file && file.type.startsWith('image/');
};

/**
 * Sanitizes filename to remove problematic characters
 * @param {string} fileName - Original filename
 * @returns {string} Sanitized filename
 */
export const sanitizeFileName = (fileName) => {
    if (!fileName) return 'untitled';

    return (
        fileName
            .trim()
            .replace(/[<>:"/\\|?*]/g, '_')
            .replace(/\s+/g, '_')
            .replace(/^[_.]+|[_.]+$/g, '')
            .substring(0, MAX_FILENAME_LENGTH) || 'untitled'
    );
};
